import { useCallback } from "react";

import { skipToken } from "@reduxjs/toolkit/query";
import {
    AbfLoanExpanded,
    getZcLoanLiquidationThreshold,
    useAbfFetches,
    useRefinanceConstraintQuery
} from "@bridgesplit/abf-react";
import { bpsToPercent, LOADING_ERROR, Result, uiPercentToBps } from "@bridgesplit/utils";
import { useGetSetRefinanceConstraintWithSignature } from "app/components/transactions";

export function useRefinanceProtection(loanExpanded: AbfLoanExpanded | undefined) {
    const loanAddress = loanExpanded?.loan.address;
    const { data: constraint, isLoading } = useRefinanceConstraintQuery(loanAddress ?? skipToken);
    const setRefinanceConstraint = useGetSetRefinanceConstraintWithSignature();
    const { resetMarketsPublicApi, resetLoanApi } = useAbfFetches();

    const currentLtv = getZcLoanLiquidationThreshold(loanExpanded);

    const floorPercent = constraint?.minLiquidationThreshold
        ? bpsToPercent(constraint.minLiquidationThreshold)
        : undefined;

    const submit = useCallback(
        async (thresholdPercent: number | undefined): Promise<Result<void>> => {
            if (!loanAddress) return Result.errFromMessage(LOADING_ERROR);
            const minLiquidationThreshold = thresholdPercent !== undefined ? uiPercentToBps(thresholdPercent) : 0;
            const res = await setRefinanceConstraint({ loanAddress, minLiquidationThreshold });
            if (res.isOk()) {
                resetMarketsPublicApi();
                resetLoanApi();
            }
            return res;
        },
        [loanAddress, setRefinanceConstraint, resetMarketsPublicApi, resetLoanApi]
    );

    return { currentLtv, floorPercent, submit, isLoading };
}
